import * as tslib_1 from "tslib";
import { ErrorHandler } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject } from 'rxjs';
import * as padStart from 'lodash/padStart';
import { environment } from '../../../environments/environment';
import { AuthEventType } from '../models/auth-event.model';
/**
 * @const
 * Cushion time (in seconds) for retrieving a new access token using the refresh token. A new access
 * token will be retrieved REFRESH_TOKEN_BUFFER seconds prior to the access token's expiration.
 */
var REFRESH_TOKEN_BUFFER = 30;
/**
 * Authentication service
 */
var AuthService = /** @class */ (function () {
    function AuthService(http, _cookies, _errorHandler) {
        this.http = http;
        this._cookies = _cookies;
        this._errorHandler = _errorHandler;
        this._authStream = new BehaviorSubject(null);
        this._tokenStream = new BehaviorSubject(null);
        /**
         * Encoded access token sent with API requests.
         */
        this._accessToken = null;
        /**
         * Refresh token (used to get new access token when the old one is about to expire)
         */
        this._refreshToken = null;
    }
    Object.defineProperty(AuthService.prototype, "authStream", {
        /**
         * Event stream of authentication events (sign in, sign out, etc.)
         */
        get: function () {
            return this._authStream;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AuthService.prototype, "tokenStream", {
        /**
         * Token stream
         */
        get: function () {
            return this._tokenStream;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AuthService.prototype, "authenticated", {
        /**
         * Returns true if the access token is still valid.
         */
        get: function () {
            return !!this._accessToken;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AuthService.prototype, "endUser", {
        /**
         * Returns true if authenticated as an end user (as opposed to a client)
         */
        get: function () {
            return this._endUser;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AuthService.prototype, "token", {
        /**
         * Returns the access token (if available).
         */
        get: function () {
            return this._accessToken;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AuthService.prototype, "recid", {
        get: function () {
            return this._ticket.recid;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Sends an authentication request to the authorization API to retrieve an access token. For anonymous access, this
     * logs in using client credentials; if the user is logged in to the AOPA.org website, this uses the resource owner
     * password credentials (ROPC) flow, using the AOPA ticket cookie as the password.
     */
    AuthService.prototype.login = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var ticketCookie, request, result;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        ticketCookie = this._cookies.get('Ticket');
                        if (ticketCookie) {
                            this._ticket = this.parseTicket(ticketCookie);
                            this._endUser = true;
                            this._clientId = environment.clientIdUser;
                            this._clientSecret = environment.clientSecretUser;
                            this._scope = environment.authScopesUser;
                            request = {
                                grant_type: 'password',
                                client_id: this._clientId,
                                client_secret: this._clientSecret,
                                username: this._ticket.username,
                                password: ticketCookie,
                                scope: this._scope
                            };
                        }
                        else {
                            this._endUser = false;
                            this._clientId = environment.clientIdAnon;
                            this._clientSecret = environment.clientSecretAnon;
                            this._scope = environment.authScopesAnon;
                            request = {
                                grant_type: 'client_credentials',
                                client_id: this._clientId,
                                client_secret: this._clientSecret,
                                scope: this._scope
                            };
                        }
                        return [4 /*yield*/, this._authenticate(request)];
                    case 1:
                        result = _a.sent();
                        if (result.success) {
                            this._tokenStream.next(this._accessToken);
                            this._authStream.next({
                                type: AuthEventType.SignIn
                            });
                        }
                        else {
                            this._tokenStream.next(null);
                            this._authStream.next({
                                type: AuthEventType.InvalidSignIn
                            });
                        }
                        return [2 /*return*/, result];
                }
            });
        });
    };
    /**
     * Method for redirecting user to the login page.
     */
    AuthService.prototype.redirectToLogin = function () {
        window.location.href = environment.loginUrl;
    };
    /**
     * Logs off the current user by deleting the tokens
     */
    AuthService.prototype.logout = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                this._accessToken = null;
                this._refreshToken = null;
                this._tokenStream.next(null);
                this._authStream.next({
                    type: AuthEventType.SignOut
                });
                return [2 /*return*/];
            });
        });
    };
    /**
     * Parses the AOPA ticket cookie
     * @param {string} ticket Ticket cookie value as a string
     * @returns {ITicket} Ticket info
     */
    AuthService.prototype.parseTicket = function (ticket) {
        var result = {};
        var decoded = decodeURIComponent(ticket);
        var parts = decoded.split('&');
        for (var i = 0; i < parts.length; i += 2) {
            var key = parts[i];
            var value = parts[i + 1];
            switch (key) {
                case 'user':
                    result.username = value;
                    break;
                case 'loop':
                    var loop = parseInt(value, 10);
                    if (loop && !isNaN(loop)) {
                        var recid = (loop / 2).toString();
                        result.recid = padStart(recid, 8, '0');
                    }
                    break;
            }
        }
        return result;
    };
    /**
     * Retrieves a new access token using the refresh token.
     */
    AuthService.prototype._refresh = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this._refreshToken) {
                            throw new Error('Missing refresh token');
                        }
                        return [4 /*yield*/, this._authenticate({
                                grant_type: 'refresh_token',
                                refresh_token: this._refreshToken,
                                client_id: this._clientId,
                                client_secret: this._clientSecret,
                                scope: this._scope
                            })];
                    case 1: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    /**
     * Sends an authentication request to the server
     * @param request Authentication request parameters
     * @param [refresh] True to schedule a refresh of the access token prior to its expiration
     */
    AuthService.prototype._authenticate = function (request, refresh) {
        if (refresh === void 0) { refresh = true; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var url, options, params, result, data, e_1, r;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        url = environment.authUrl + "/connect/token";
                        options = { headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded') };
                        params = new HttpParams();
                        Object.keys(request).forEach(function (param) {
                            var value = request[param];
                            params = params.append(param, value);
                        });
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.http.post(url, params, options).toPromise()];
                    case 2:
                        data = _a.sent();
                        if (!data || !data.access_token) {
                            throw new Error('Missing token from response');
                        }
                        result = {
                            success: true,
                            accessToken: data.access_token,
                            refreshToken: data.refresh_token,
                            expiresIn: data.expires_in
                        };
                        return [3 /*break*/, 4];
                    case 3:
                        e_1 = _a.sent();
                        if (e_1 && e_1.json) {
                            r = e_1.json();
                            return [2 /*return*/, {
                                    success: false,
                                    error: r.error,
                                    errorDescription: r.error_description
                                }];
                        }
                        throw e_1;
                    case 4:
                        // Store access token
                        if (result.accessToken) {
                            this._accessToken = result.accessToken;
                            // Cancel any existing pending refresh of the access token
                            this._cancelRefresh();
                        }
                        else {
                            throw new Error('Missing token from response');
                        }
                        // Store refresh token
                        if (result.refreshToken) {
                            this._refreshToken = result.refreshToken;
                            // Schedule a refresh of the access token prior to its expiration
                            if (refresh) {
                                this._scheduleRefresh(result.expiresIn);
                            }
                        }
                        return [2 /*return*/, result];
                }
            });
        });
    };
    /**
     * Schedules a refresh of the access token prior to its expiration. If the user is not
     * authenticated, or the access token is already expired, the refresh will occur immediately.
     * @param expiresIn {Number} The lifetime in seconds of the access token.
     */
    AuthService.prototype._scheduleRefresh = function (expiresIn) {
        var _this = this;
        this._cancelRefresh();
        var delay = 0;
        if (this.authenticated) {
            delay = Math.max(expiresIn - REFRESH_TOKEN_BUFFER, 0);
        }
        this._refreshTimer = setTimeout(function () { return _this.login(); }, delay * 1000);
    };
    /**
     * Cancels any pending refresh of the access token.
     */
    AuthService.prototype._cancelRefresh = function () {
        if (this._refreshTimer) {
            clearTimeout(this._refreshTimer);
        }
    };
    return AuthService;
}());
export { AuthService };
